<script setup lang="ts">
import DOMPurify from 'dompurify';
import {
  computed,
  ref,
  toRef,
} from 'vue';

import VDialog from '@components/dialogs/VDialog/VDialog.vue';
import {
  useI18n,
  useLocale,
} from '@modules/i18n/composables';
import {
  STATIC_PAGES_CONFIG,
  TRANSLATION_GROUP_STATIC_PAGES,
} from '@modules/static-pages/constants';
import { EStaticPageName } from '@modules/static-pages/enums';
import { useStaticPageQuery } from '@modules/static-pages/queries';

import type { StaticPageDialogButtonProps } from './types';

const props = defineProps<StaticPageDialogButtonProps>();

const { currentLocale } = useLocale();
const { getTranslation } = useI18n();

const activeStaticPage = toRef(props, 'page');
const isDialogVisible = ref(false);

const {
  data: staticPage,
  isLoading: isLoadingStaticPage,
} = useStaticPageQuery(activeStaticPage, currentLocale, isDialogVisible);
const isStaticPageDialogVisible = computed(() => !isLoadingStaticPage.value && isDialogVisible.value);
const staticPageTitle = computed(() => {
  if (activeStaticPage.value === EStaticPageName.PRIVACY) {
    return getTranslation(TRANSLATION_GROUP_STATIC_PAGES, 'PRIVACY.HEADER').value;
  }

  if (activeStaticPage.value === EStaticPageName.TERMS) {
    return getTranslation(TRANSLATION_GROUP_STATIC_PAGES, 'TERMS.HEADER').value;
  }

  return undefined;
});

const label = computed(() => {
  if (activeStaticPage.value === EStaticPageName.PRIVACY) {
    return getTranslation(TRANSLATION_GROUP_STATIC_PAGES, 'ATTENTION_SECOND_PART').value;
  }

  if (activeStaticPage.value === EStaticPageName.TERMS) {
    return getTranslation(TRANSLATION_GROUP_STATIC_PAGES, 'ATTENTION_LAST_PART').value;
  }

  return undefined;
});

function setDialogVisible(visibility: boolean): void {
  isDialogVisible.value = visibility;
}
</script>

<template>
  <!-- eslint-disable vue/no-v-html -->
  <span v-data-qa-auto-id="'StaticPageDialogButton'"
    v-if="$hasPermission(STATIC_PAGES_CONFIG[activeStaticPage].permission)"
    v-data-test="{ el: 'static-page-btn', page: activeStaticPage }"
    class="cursor-pointer text-primary underline"
    @click.stop="setDialogVisible(true)"
  >
    {{ label }}
  </span>
  <VDialog
    :title="staticPageTitle"
    :visible="isStaticPageDialogVisible"
    @hide="setDialogVisible(false)"
  >
    <div
      v-html="DOMPurify.sanitize(staticPage?.content || '')"
    />
  </VDialog>
</template>
